import moment from 'moment';
import { businessProcessShapeClasses } from '../businessProcess/shapes';
import { isShape, isWireframe } from '../objects/guards';
import { type ShapeName, shapeNameToClass } from '../shapes';
import { WIREFRAME_PRIMARY_COLOR, WIREFRAME_TEXT_COLOR } from './wireframes';
import { STROKE_TYPES } from '../constants';

const NO_DEFAULT_FOR_KEYS = ['top', 'left'] as const;

export type Palette = string[];

export const defaultColorPalette = [
  'transparent',
  '#FFFFFF',
  '#BDBDBD',
  '#FAEBB9',
  '#A9D0DB',
  '#B1D4F5',
  '#C7DBAF',
  '#BFB0CF',
  '#FAC0AF',
  '#FFE48E',
  '#FCC98D',
  '#6EA6A9',
  '#FA9678',
  '#5CA5E0',
  '#FCA841',
  '#7BBA34',
  '#C47C93',
  '#7F6B91',
  '#7D3C00',
  '#333333',
];

export const noteColorPalette = [
  '#FAEBB9',
  '#FFE594',
  '#FFD0C5',
  '#FAB1A0',
  '#FF9998',
  '#FF7675',
  '#FF9BBF',
  '#FD79A8',
  '#B6B0FF',
  '#A29BFE',
  '#A7D3FF',
  '#74B9FF',
  '#91F3F3',
  '#53D3D3',
  '#6DEBC8',
  '#30CCA0',
];

export const colorPaletteForText = (): Palette => {
  return defaultColorPalette;
};

export const colorPaletteForObject = (object?: fabric.Object): Palette => {
  if (object && isShape(object)) {
    switch (object.shapeName) {
      case 'note':
        return noteColorPalette;
      case 'video':
        return defaultColorPalette.map(color =>
          color === 'transparent' ? '#F1F1F1' : color
        );
    }
  }

  return defaultColorPalette;
};

export const defaultColorForShape = shapeName => {
  if (shapeName === 'note') {
    return '#FAEBB9';
  } else {
    return '#FFFFFF';
  }
};

// declared here to avoid circular imports accessing via the Connector class static properties
export const CONNECTOR_DEFAULT_WIDTH = 2;
export const CONNECTOR_ROUTING_ORTHOGONAL_AUTO = 20;

// Track the default properties that should be used for newly created objects.
// Defaults are grouped by "category" which is a combination of the object
// class (Shape, Frame, Connector) and the shapeName, so that most shapes can
// share defaults, with some special exceptions (like 'note' & 'text' shapes).
class DefaultsManager {
  configurations: any = {};
  defaultConfigurations: any = {};

  // Define the defaults for each objects.
  constructor() {
    // Basic objects
    this.configurations.frame = {
      fill: '#FFFFFF',
      strokeWidth: 0,
    };
    this.configurations.layout = {
      layoutSizeHorizontal: 'user',
      layoutSizeVertical: 'user',
      layoutGap: 10,
      layoutPadding: 10,
      layoutDirection: 'vertical',
      layoutAlignHorizontal: 'center',
      layoutAlignVertical: 'top',
    };
    this.configurations.stack = {
      fill: '#FFFFFF',
      stroke: '#AAAAAA',
      strokeWidth: 2,
    };
    this.configurations.table = {
      fill: '#FFFFFF',
      stroke: '#999999',
      strokeWidth: 1,
    };
    this.configurations.stamp = {};
    this.configurations.textField = {
      fill: null,
      stroke: null,
    };
    this.configurations.embeddedBookmark = {
      fill: '#FFFFFF',
    };
    this.configurations.connector = {
      stroke: '#0073CF',
      strokeWidth: CONNECTOR_DEFAULT_WIDTH,
      lineRouting: CONNECTOR_ROUTING_ORTHOGONAL_AUTO,
      strokeType: STROKE_TYPES.SOLID,
    };
    this.configurations.device = {
      fill: 'transparent',
      orientation: 'portrait',
      showFramed: true,
    };

    // Shape subclasses
    this.configurations.shape = {
      stroke: '#BDBDBD',
      fill: '#FFFFFF',
      strokeWidth: 2,
    };
    this.configurations.shape_note = {
      ...this.configurations.shape,
      fill: '#FAEBB9',
      stroke: null,
    };
    this.configurations.shape_text = {
      ...this.configurations.shape,
      textAlign: 'left',
      fill: null,
      stroke: null,
      width: 30,
      height: 30,
    };
    this.configurations.shape_icon = {
      ...this.configurations.shape,
      fill: '#333333',
      stroke: null,
    };
    this.configurations.shape_spriteIcon = {
      ...this.configurations.shape,
      fill: null,
      stroke: null,
      strokeWidth: null,
    };
    this.configurations.shape_annotation = {
      ...this.configurations.shape,
      textContent:
        '<p><span style="color:#FFFFFF">Double-click to edit</span></p>',
      textOpacity: 0.9,
      fill: '#FCA841',
      stroke: '#FCA841',
    };
    this.configurations.shape_progressBar = {
      ...this.configurations.shape,
      textContent: '<p>Double-click to edit</p>',
      fill: '#5ca5e0',
      progress: 0.5,
    };
    this.configurations.shape_milestone = {
      ...this.configurations.shape,
      textContent: '<p>Double-click to edit</p>',
      fill: '#6EA6A9',
    };
    const defaultStartDate = moment().startOf('month');
    this.configurations.shape_timeline = {
      ...this.configurations.shape,
      timescale: 'auto',
      startDate: defaultStartDate.unix(),
      endDate: defaultStartDate.add(3, 'months').unix(),
      fiscalMonth: 1,
      fiscalYearFormat: 'both_years_quarter_last',
      strokeWidth: null,
    };

    // Wireframe subclasses.
    this.configurations.wireframe = {
      objectSize: 'medium',
      fill: WIREFRAME_PRIMARY_COLOR,
      textColor: WIREFRAME_TEXT_COLOR,
      fontSize: 14,
    };
    this.configurations.wireframe_button = {
      ...this.configurations.wireframe,
      style: 'squareFilled',
      textColor: null,
    };
    this.configurations.wireframe_link = {
      ...this.configurations.wireframe_button,
      style: 'link',
    };
    this.configurations.wireframe_navMenu = {
      ...this.configurations.wireframe,
      showIcons: true,
      showSeparators: true,
      textColor: WIREFRAME_TEXT_COLOR,
    };
    this.configurations.wireframe_navMenuVertical = {
      ...this.configurations.wireframe_navMenu,
    };
    this.configurations.wireframe_navMenuHorizontal = {
      ...this.configurations.wireframe_navMenu,
    };
    this.configurations.wireframe_multiSelect = {
      ...this.configurations.wireframe,
      style: 'round',
      textColor: '#FFFFFF',
      showIcons: true,
    };
    this.configurations.wireframe_inputField = {
      ...this.configurations.wireframe,
      textColor: WIREFRAME_TEXT_COLOR,
      style: 'framed',
    };
    this.configurations.wireframe_datePicker = {
      ...this.configurations.wireframe_inputField,
    };
    this.configurations.wireframe_searchBar = {
      ...this.configurations.wireframe_inputField,
    };
    this.configurations.wireframe_dropdown = {
      ...this.configurations.wireframe_inputField,
      style: 'framedWithPanel',
    };
    this.configurations.wireframe_checkbox = {
      ...this.configurations.wireframe,
      componentState: 'checkbox',
      label: { value: 'Label', position: 'right' },
    };
    this.configurations.wireframe_toggle = {
      ...this.configurations.wireframe,
      style: 'roundFilled',
      componentState: 'off',
      label: { value: 'Label', position: 'right' },
    };
    this.configurations.wireframe_radioButton = {
      ...this.configurations.wireframe,
      componentState: 'filled',
      label: { value: 'Label', position: 'right' },
    };
    this.configurations.wireframe_slider = {
      ...this.configurations.wireframe,
      progress: 0.5,
    };
    this.configurations.wireframe_pill = {
      ...this.configurations.wireframe,
      showBackground: true,
      showOutline: true,
    };
    this.configurations.wireframe_avatar = {
      ...this.configurations.wireframe,
      showBackground: true,
      showOutline: true,
    };

    this.configurations.wireframe_scrollBar = {
      ...this.configurations.wireframe,
      showOutline: true,
      style: 'vertical_round',
      progress: 0.3,
    };

    this.configurations.wireframe_pagination = {
      ...this.configurations.wireframe,
      style: 'framed',
      showIcons: true,
      showButtonText: true,
      showEllipses: true,
      buttonLeft: 'Prev',
      buttonRight: 'Next',
    };

    this.configurations.wireframe_breadcrumb = {
      ...this.configurations.wireframe,
      style: 'slash',
    };

    this.configurations.wireframe_progressStepper = {
      ...this.configurations.wireframe,
      textColor: '#FFFFFF',
      showIcons: true,
      showStepText: true,
      showStepNumbers: true,
    };
    this.configurations.shape_smartRecord = {
      stroke: null,
      recordType: 'features',
    };

    // TODO: Paths are not handled yet.

    this.defaultConfigurations = JSON.parse(
      JSON.stringify(this.configurations)
    );
  }

  // Call this to get the default property or what it has been overwritten with
  // by setFor.
  for(objectType: string, shapeName?: string) {
    const category = DefaultsManager.objectDefaultCategory(
      objectType,
      shapeName
    );

    if (!category) {
      console.error('No category for', objectType, shapeName);
      return {};
    }

    const defaults =
      objectType === 'shape' && shapeName
        ? shapeNameToClass(shapeName as ShapeName)?.defaultProperties
        : undefined;

    return {
      ...defaults,
      ...this.configurations[category],
    };
  }

  // Gets the default property value, ignoring anything set in setFor.
  defaultFor(objectType, shapeName?: string) {
    const category = DefaultsManager.objectDefaultCategory(
      objectType,
      shapeName
    );
    if (!category || !this.defaultConfigurations.hasOwnProperty(category)) {
      return {}; // For backwards compatibility don't throw an error if we don't have defaults.
    }

    return this.defaultConfigurations[category];
  }

  setFor(object: fabric.Object, key: string, value: unknown) {
    if (NO_DEFAULT_FOR_KEYS.includes(key)) {
      return;
    }

    const category = DefaultsManager.objectDefaultCategory(
      object.type,
      isShape(object)
        ? object.shapeName
        : isWireframe(object)
        ? object.wireframeName
        : undefined
    );

    if (!category) {
      return;
    }

    this.setForCategory(category, key, value);
  }

  setForCategory(category: string, key: string, value: unknown) {
    this.configurations[category] ??= {};
    this.configurations[category][key] = value;
  }

  // Determine the category that will be used for a new object with the
  // given class and shape.
  static objectDefaultCategory(objectType: string, name?: string) {
    switch (objectType) {
      case 'shape':
        switch (name) {
          case 'note':
          case 'text':
          case 'icon':
          case 'annotation':
          case 'progressBar':
          case 'milestone':
          case 'timeline':
          case 'spriteIcon':
          case 'smartRecord':
            return `${objectType}_${name}`;
          default: {
            if (name && name in businessProcessShapeClasses) {
              return 'shape_businessProcess';
            } else {
              return objectType;
            }
          }
        }

      case 'wireframe':
        switch (name) {
          case 'button':
          case 'multiSelect':
          case 'link':
          case 'inputField':
          case 'checkbox':
          case 'datePicker':
          case 'searchBar':
          case 'dropdown':
          case 'navMenuVertical':
          case 'navMenuHorizontal':
          case 'radioButton':
          case 'toggle':
          case 'slider':
          case 'pill':
          case 'avatar':
          case 'scrollBar':
          case 'pagination':
          case 'breadcrumb':
          case 'progressStepper':
            return `${objectType}_${name}`;

          default:
            return objectType;
        }

      case 'frame':
      case 'layout':
      case 'table':
      case 'embeddedBookmark':
      case 'connector':
      case 'textField':
      case 'device':
      case 'stamp':
        return objectType;
    }
  }
}

export default DefaultsManager;
